// ============================================================================
// DAS RASTER HINTER DEM TERMINKALENDER - REINE REGELN, OHNE JSX.
// ============================================================================
// ORIGINALTEXT MARVIN (03.09.2026)
//   „die terminsteuerung ist immernoch nicht so wie ich sie dir in den bildern
//    gezeigt habe und wie ich sie brauche. behebe den fehler final!"
//
// DIE ALTE TERMINSTEUERUNG WAR EIN KALENDER UND KEIN FORMULAR. Ein Monatsgitter
// links, die halbstündigen Zeiten rechts, darunter die Leiste der gewählten
// Tage. Wer einen Termin vergibt, fragt zuerst „wann ist überhaupt Platz" - und
// diese Frage beantwortet ein Datumsfeld nicht: es nimmt jeden Tag an, auch den
// Sonntag, auch den, an dem die Kolonne schon woanders gräbt.
//
// WARUM DIESE DATEI OHNE JSX AUSKOMMT: eine Datei mit JSX lässt sich mit
// `--experimental-strip-types` nicht laden, und dann bliebe einem Prüfstand nur
// die Textsuche - **und eine Textsuche misst, dass etwas dasteht, nicht, dass
// es stimmt.** Dieselbe Aufteilung wie bei `namen.ts`, `ordnerabwurf.ts` und
// `bildverkleinern.ts`.
//
// **UND HIER STEHT KEIN EINZIGER FEIERTAG.** Welcher Tag keiner ist, weiss der
// Server: es hängt am Bundesland der Auftrags-PLZ, und der Browser kennt es
// nicht. Was hier passiert, ist ANWENDEN einer Liste, die von dort kommt - nie
// Rechnen. `naechsterWerktag` bekommt die Tage übergeben und kennt von sich aus
// nur Samstag und Sonntag, also genau das, was in jedem Kalender der Welt
// gleich ist.
// ============================================================================

import { tageText } from './fristtext.ts';

/** Ein halbstündiges Zeitfenster ist die kleinste Einheit dieses Hauses. */
export const SCHRITT = 30;

/** „07:30" wird 450. Was nicht wie eine Uhrzeit aussieht, gibt 0. */
export function zuMin(u: string): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(u ?? ''));
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** 450 wird „07:30". */
export function ausMin(m: number): string {
  const t = ((Math.round(m) % 1440) + 1440) % 1440;
  const h = Math.floor(t / 60);
  return String(h).padStart(2, '0') + ':' + String(t % 60).padStart(2, '0');
}

/**
 * Die Anfangszeiten zwischen von und bis, in Schritten von einer halben Stunde.
 *
 * Der LETZTE Slot fängt eine halbe Stunde VOR dem Ende an: „07:00 bis 16:00"
 * sind achtzehn Fenster, das letzte um 15:30. Wer bis 16:00 mitzählte, böte ein
 * Fenster an, das erst um 16:30 zu Ende wäre.
 */
export function slotliste(von: string, bis: string): string[] {
  const a = zuMin(von);
  const e = zuMin(bis);
  if (!(e > a)) return [];
  const aus: string[] = [];
  for (let m = a; m + SCHRITT <= e; m += SCHRITT) aus.push(ausMin(m));
  return aus;
}

/**
 * Aus den gewählten Fenstern die Spanne, die gebucht wird.
 *
 * Das Ende ist das Ende des LETZTEN Fensters, nicht sein Anfang. Ohne diese
 * halbe Stunde wäre ein einzeln angeklicktes Fenster ein Termin von null
 * Minuten, und der Server weist ihn zu Recht ab („endet, bevor er anfängt").
 */
export function spanne(slots: string[]): { von: string; bis: string } | null {
  if (!slots.length) return null;
  const s = [...slots].sort((a, b) => zuMin(a) - zuMin(b));
  return { von: s[0], bis: ausMin(zuMin(s[s.length - 1]) + SCHRITT) };
}

/**
 * Alle Fenster zwischen zwei Anklickpunkten, einschliesslich beider.
 *
 * So arbeitet die Auswahl im Bestand: der erste Klick setzt den Anker, der
 * zweite spannt auf. Ein Ziehen mit der Maus gibt es nicht - auf dem Telefon
 * wäre es kein Weg, und dort steht der Monteur.
 */
export function spanneZwischen(
  alle: string[], a: string, b: string
): string[] {
  const i = alle.indexOf(a);
  const j = alle.indexOf(b);
  if (i < 0 || j < 0) return [];
  return alle.slice(Math.min(i, j), Math.max(i, j) + 1);
}

/**
 * WAS EIN KLICK AUF EIN ZEITFENSTER BEDEUTET.
 *
 * ORIGINALTEXT MARVIN (07.09.2026, mit einem Bildschirmfoto)
 *   „bei der Terminvergabe kann ich nicht einfach zeiten frei selektieren, er
 *    markiert immer die uhrzeiten davor auch."
 *
 * ER HAT RECHT, UND ES WAR IM BROWSER ABZULESEN. Gemessen am 07.09.2026 an
 * einem echten Tag: beim Öffnen sind ALLE sechzehn Fenster gewählt (der Block
 * „Ganzer Tag"), ein Klick auf 13:00 lässt 09:00 bis 12:30 stehen, und ein
 * zweiter auf 15:00 wirft alles weg bis auf 15:00. Aus seiner Sicht wählt
 * jeder Klick etwas anderes aus als das, was er angeklickt hat.
 *
 * **DIE ALTE REGEL WAR EINE SPANNE, UND EINE SPANNE HAT KEINE EINZELNEN
 * FENSTER.** Sie ist am 03.09.2026 aus einer anderen Beanstandung entstanden
 * („selektieren/deselektieren ... anstatt wegzuklicken") und hat die eine
 * Hälfte behoben und die andere gebaut: abwählen ging, frei wählen nicht.
 *
 * DREI REGELN, UND DIE ERSTE IST DIE NEUE:
 *
 *   * Die Zeit steht noch so, wie ein BLOCKKNOPF sie gesetzt hat
 *     (`vonHand` ist falsch): der Klick setzt sie NEU auf diese halbe
 *     Stunde. Der Block ist ein Vorschlag, und der erste Handgriff ersetzt
 *     ihn - sonst müsste man aus „Ganzer Tag" fünfzehn Fenster einzeln
 *     wegklicken, um eine Stunde zu buchen.
 *   * Danach wählt ein Klick sein Fenster DAZU.
 *   * Ein Klick auf ein gewähltes nimmt es WEG.
 *
 * UND DAS LETZTE FENSTER BLEIBT STEHEN. Ein Termin ohne Zeit ist keiner;
 * abgewählt wird der TAG, und zwar im Kalender. Ein Zustand, aus dem heraus
 * der Knopf grau ist und niemand weiss warum, ist schlimmer als ein Klick,
 * der nichts tut.
 *
 * EIN BELEGTES FENSTER WIRD NICHT GEWÄHLT. Es ist der einzige Fall, in dem
 * ein Klick gar nichts tut - und der Knopf ist ohnehin gesperrt.
 *
 * **WAS DIESE REGEL NICHT MEHR VERHINDERT, VERHINDERT `luecken`.** Frei
 * wählen heisst: die Auswahl kann Löcher haben, und ein Termin hat keine.
 * Der Riegel liegt deshalb nicht mehr im Klick, sondern am Knopf - dort, wo
 * er einen Satz danebenschreiben kann.
 */
export function zeitKlick(
  alle: string[], gewaehlt: string[], fenster: string, belegt: Set<string>,
  vonHand = true
): string[] {
  if (belegt.has(fenster)) return gewaehlt;
  if (alle.indexOf(fenster) < 0) return gewaehlt;

  const g = gewaehlt.filter((s) => alle.includes(s));
  if (!vonHand || !g.length) return [fenster];

  const sortiert = (l: string[]) =>
    [...l].sort((a, b) => zuMin(a) - zuMin(b));

  if (g.includes(fenster)) {
    if (g.length === 1) return sortiert(g);
    return sortiert(g.filter((s) => s !== fenster));
  }
  return sortiert([...g, fenster]);
}

/**
 * DIE FENSTER, DIE ZWISCHEN DER AUSWAHL FEHLEN.
 *
 * Ein Termin hat einen Anfang und ein Ende und nichts dazwischen: wer 13:00
 * und 15:00 wählt und 13:30 auslässt, bucht in Wahrheit 13:00 bis 15:30 -
 * **also zwei Stunden, die er nicht angeklickt hat.** Solange die Auswahl
 * eine Spanne war, konnte das nicht vorkommen; seit man frei wählt, sehr
 * wohl.
 *
 * ES WIRD NICHT HEIMLICH GESCHLOSSEN UND NICHT HEIMLICH GEBUCHT. Die Lücke
 * wird BENANNT, der Knopf sagt es, und daneben steht der Handgriff, der sie
 * schliesst. Ein Bildschirm, der aus einer Auswahl mit Loch stillschweigend
 * eine volle Spanne macht, bucht Zeit, die niemand gewählt hat.
 *
 * **EIN BELEGTES FENSTER IN DER LÜCKE ZÄHLT MIT, und zwar gerade weil es
 * sich nicht wählen lässt.** Wer es hier wegliesse, meldete „keine Lücke"
 * über einer Spanne, die den Termin einer anderen Firma überdeckt - der
 * Knopf wäre offen, und der Server wiese die Buchung erst NACH dem Klick ab.
 * Ob eine Lücke sich schliessen lässt, entscheidet der Bildschirm daneben:
 * er hat die belegten ohnehin und schreibt den Unterschied hin.
 */
export function luecken(
  alle: string[], gewaehlt: string[]
): string[] {
  const g = gewaehlt.filter((s) => alle.includes(s))
    .sort((a, b) => zuMin(a) - zuMin(b));
  if (g.length < 2) return [];
  const von = alle.indexOf(g[0]);
  const bis = alle.indexOf(g[g.length - 1]);
  return alle.slice(von, bis + 1).filter((s) => !g.includes(s));
}

/**
 * Welche Fenster eines Tages schon belegt sind.
 *
 * Ein Termin von 07:00 bis 12:00 belegt zehn Fenster und nicht eines. Wer nur
 * die Anfangszeit als belegt zeichnet, zeigt einen Tag, der frei aussieht und
 * es nicht ist - und der nächste bucht dort einen zweiten Bagger.
 */
export function belegteFenster(
  eintraege: { von?: string | null; bis?: string | null }[]
): Set<string> {
  const aus = new Set<string>();
  for (const e of eintraege) {
    const a = zuMin(e.von ?? '');
    // OHNE ENDZEIT GILT EINE HALBE STUNDE. Eine Buchung ohne Ende ist eine
    // Angabe, die es gibt; ein ganzer Tag daraus wäre eine, die niemand
    // gemacht hat.
    const e2 = e.bis ? zuMin(e.bis) : a + SCHRITT;
    for (let m = a; m < e2; m += SCHRITT) aus.add(ausMin(m));
  }
  return aus;
}

// ---------------------------------------------------------------- Der Monat

/** „2026-09" wird zu den ISO-Tagen dieses Monats. */
export function tageImMonat(monat: string): string[] {
  if (!/^\d{4}-\d{2}$/.test(monat)) return [];
  const [j, m] = monat.split('-').map(Number);
  const wieviele = new Date(Date.UTC(j, m, 0)).getUTCDate();
  const aus: string[] = [];
  for (let t = 1; t <= wieviele; t++) {
    aus.push(`${monat}-${String(t).padStart(2, '0')}`);
  }
  return aus;
}

/**
 * Wie viele Leerfelder vor dem Ersten stehen, damit MONTAG links steht.
 *
 * Der Sonntag ist in JavaScript die 0 und in einem deutschen Kalender der
 * siebte Tag. Wer das übersieht, schiebt jeden Monat um einen Tag - und der
 * Fehler fällt nur an den Monaten auf, die an einem Sonntag beginnen.
 */
export function fuehrendeFelder(monat: string): number {
  const t = tageImMonat(monat)[0];
  if (!t) return 0;
  return (new Date(t + 'T00:00:00Z').getUTCDay() + 6) % 7;
}

/** Der Vormonat und der Folgemonat, als „JJJJ-MM". */
export function monatVerschieben(monat: string, um: number): string {
  if (!/^\d{4}-\d{2}$/.test(monat)) return monat;
  const [j, m] = monat.split('-').map(Number);
  const d = new Date(Date.UTC(j, m - 1 + um, 1));
  return d.toISOString().slice(0, 7);
}

/** Der Monat, in dem dieser Tag liegt. */
export function monatVon(iso: string): string { return String(iso).slice(0, 7); }

/** Ein Tag weiter. */
export function tagPlus(iso: string, um = 1): string {
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + um);
  return d.toISOString().slice(0, 10);
}

/** Samstag oder Sonntag. Das ist das Einzige, was ein Browser von selbst weiss. */
export function istWochenende(iso: string): boolean {
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return false;
  const w = d.getUTCDay();
  return w === 0 || w === 6;
}

/**
 * Der nächste Arbeitstag nach diesem.
 *
 * **DIE FEIERTAGE KOMMEN HEREIN, SIE ENTSTEHEN NICHT HIER.** `frei` ist die
 * Liste, die der Server für den angezeigten Monat und seine Nachbarn geschickt
 * hat; welcher Tag darin steht, entscheidet das Bundesland der Auftrags-PLZ.
 * Reicht die Liste nicht (mehr als ein Monat über den Rand hinaus), wird
 * NICHTS geraten: dann kommt `''` zurück, und die Oberfläche sagt es.
 */
export function naechsterWerktag(
  iso: string, frei: Set<string> | string[], bis?: string
): string {
  const menge = frei instanceof Set ? frei : new Set(frei);
  let t = tagPlus(iso);
  for (let i = 0; i < 60 && t; i++) {
    if (bis && t > bis) return '';
    if (!istWochenende(t) && !menge.has(t)) return t;
    t = tagPlus(t);
  }
  return '';
}

/**
 * Die Kette aus n Arbeitstagen ab dem Starttag, EINSCHLIESSLICH des Starttags.
 *
 * Der Starttag zählt mit und wird NICHT verschoben - das ist die Entscheidung
 * dessen, der ihn angeklickt hat. Dieselbe Regel wie in `bautageAb` auf dem
 * Server; hier wird sie auf die Liste angewendet, die von dort kommt.
 */
export function werktagskette(
  startIso: string, wieviele: number, frei: Set<string> | string[], bis?: string
): string[] {
  if (!startIso) return [];
  const aus = [startIso];
  let t = startIso;
  for (let i = 1; i < Math.max(1, wieviele); i++) {
    t = naechsterWerktag(t, frei, bis);
    if (!t) break;
    aus.push(t);
  }
  return aus;
}

// ------------------------------------------------------------ Ein Tag im Bild

export interface Tageslage {
  /** Der Grund, aus dem der Tag so aussieht, wie er aussieht. */
  art: 'vergangen' | 'wochenende' | 'feiertag' | 'urlaub' | 'nicht_verfuegbar'
     | 'voll' | 'verfuegbar' | 'offen';
  /** Darf man ihn anklicken? */
  waehlbar: boolean;
  /** Was daneben steht, wenn jemand danach fragt. */
  grund: string;
  /** .560: ist der Tag nicht freigegeben, die Rückfrage vor dem Buchen. */
  rueckfrage?: string;
}

export interface Tagesangaben {
  iso: string;
  heute: string;
  /** Der Name des Feiertags, vom Server. Leer heisst: keiner. */
  feiertag?: string;
  /** Was die Firma gemeldet hat, oder nichts. */
  meldung?: { zustand: string; hinweis?: string | null } | null;
  /** Wie viele der Fenster dieses Tages schon belegt sind. */
  belegt: number;
  /** Wie viele es überhaupt gibt. */
  fenster: number;
}

/**
 * Wie ein Tag im Kalender dasteht.
 *
 * **EIN GRAUER TAG OHNE GRUND IST EINE SPERRE OHNE ERKLÄRUNG.** Jede Lage
 * trägt deshalb ihren Satz; die Reihenfolge geht von aussen nach innen, wie
 * beim Baustand eines Auftrags: was vorbei ist, ist vorbei, danach der
 * Kalender, danach die Meldung der Firma, zuletzt die Belegung.
 *
 * „NICHT GEMELDET" IST NICHT „VERFÜGBAR", und beide sind buchbar. Der
 * Unterschied steht in der Farbe und im Satz daneben - er ist eine Auskunft
 * und keine Sperre: der Innendienst ruft an und bucht trotzdem.
 */
export function tageslage(t: Tagesangaben): Tageslage {
  if (t.iso < t.heute) {
    return { art: 'vergangen', waehlbar: false, grund: 'vorbei' };
  }
  const z = t.meldung?.zustand;
  /* .560: GEBUCHT WERDEN KANN JEDER KÜNFTIGE TAG, DER NICHT VOLL IST, aber was
     die Firma nicht freigegeben hat, fragt vorher nach. ORIGINALTEXT MARVIN
     (30.10.2026, In der Wehring 20): „ich konnte gar keinen ausgegrauten
     Termin buchen. Da kommt noch nicht mal ein Hinweis, so von wegen, der
     fährt da nicht oder bist du dir sicher, weil das kein freigegebener
     Termin ist. Punkt 1, das sollten wir einführen für alle. Punkt 2 ... er
     hat den Samstag eingetragen für zum Fahren und das Tag ist freigegeben.
     Trotzdem kann ich den Termin nicht darauf buchen". Ein Samstag (oder
     Feiertag), den die Firma als verfügbar meldet, ist verfügbar; bis .559
     stand das Wochenende VOR der Meldung und sperrte ihn. */
  /* .561: SONNTAGS UND FEIERTAGS WIRD NICHT GEARBEITET. Marvin, 30.10.2026:
     „feiertags und sonntags wird nicht gearbeitet, aber samstags, das können
     die Firmen melden, um samstags zu arbeiten." Auch nicht mit Meldung. */
  if (t.feiertag) {
    return { art: 'feiertag', waehlbar: false, grund: t.feiertag };
  }
  if (new Date(t.iso + 'T00:00:00Z').getUTCDay() === 0) {
    return { art: 'wochenende', waehlbar: false, grund: 'Sonntag' };
  }
  if (t.fenster > 0 && t.belegt >= t.fenster) {
    return { art: 'voll', waehlbar: false, grund: 'alle Zeiten belegt' };
  }
  const wo = istWochenende(t.iso)
    ? (new Date(t.iso + 'T00:00:00Z').getUTCDay() === 6 ? 'Samstag' : 'Sonntag') : '';
  if (z === 'verfuegbar') {
    return { art: 'verfuegbar', waehlbar: true, grund: wo ? `verfügbar gemeldet (${wo})` : 'verfügbar gemeldet' };
  }
  if (z === 'urlaub') {
    return { art: 'urlaub', waehlbar: true, grund: 'Urlaub', rueckfrage: 'Die Firma hat an diesem Tag Urlaub gemeldet.' };
  }
  if (z === 'nicht_verfuegbar') {
    const g = t.meldung?.hinweis ? `nicht verfügbar: ${t.meldung.hinweis}` : 'nicht verfügbar';
    return { art: 'nicht_verfuegbar', waehlbar: true, grund: g, rueckfrage: `Die Firma hat diesen Tag als ${g} gemeldet.` };
  }
  if (wo) {
    return { art: 'wochenende', waehlbar: true, grund: wo, rueckfrage: `Die Firma hat diesen ${wo} nicht als verfügbar gemeldet.` };
  }
  return { art: 'offen', waehlbar: true, grund: 'keine Verfügbarkeit gemeldet', rueckfrage: 'Die Firma hat für diesen Tag keine Verfügbarkeit gemeldet (kein freigegebener Tag).' };
}

/**
 * Die Dauer in Tagen, wie der Bestand sie gerechnet hat: gebuchte Minuten
 * geteilt durch einen vollen Arbeitstag, auf halbe Tage gerundet.
 *
 * SIE IST EINE ANZEIGE UND KEIN WERT, DER GESPEICHERT WIRD. Was wirklich
 * gebucht wird, sind die Tage mit ihren Zeiten; diese Zahl sagt nur, worauf
 * das hinausläuft - „1,5 Tage" liest sich schneller als zwei Zeilen mit
 * Uhrzeiten.
 */
export function dauerTage(
  tage: { von: string; bis: string }[], ganz: { von: string; bis: string }
): number {
  const voll = zuMin(ganz.bis) - zuMin(ganz.von);
  if (!tage.length) return 0;
  if (voll <= 0) return tage.length;
  const summe = tage.reduce((s, t) => s + (zuMin(t.bis) - zuMin(t.von)), 0);
  const d = Math.round((summe / voll) * 2) / 2;
  return d < 0.5 ? 0.5 : d;
}

/**
 * „1,5 Tage", „2 Tage", „1 Tag". Dezimalkomma, wie überall.
 *
 * DIE FORM STEHT AN EINER STELLE, DIE ZAHL WIRD HIER GESCHRIEBEN. Ob es „Tag"
 * oder „Tage" heisst, hängt allein an der Zahl, und dieselbe Frage stellt die
 * Behördenmappe neben jeder Frist. Sie stand dort ein zweites Mal - und
 * lautete „1 Tage". `tageText` in `fristtext.ts` beantwortet sie einmal; was
 * hier bleibt, ist das Dezimalkomma, denn eine DAUER wird mit Komma
 * geschrieben und eine FRIST in ganzen Tagen.
 */
export function dauerText(d: number): string {
  const s = Number.isInteger(d) ? String(d) : String(d).replace('.', ',');
  return tageText(d, s);
}
